import React, { useState, useEffect, useCallback } from 'react';
import { StatusBadge } from '../shared';
import api from '../../utils/api';

function formatDuration(seconds) {
  if (!seconds && seconds !== 0) return '—';
  const s = Math.round(Number(seconds));
  if (isNaN(s) || s <= 0) return '—';
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}:${String(rem).padStart(2, '0')}`;
}

function capitalise(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export default function RecordingsPage({ userEmail }) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const fetchRecordings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 100, skip: 0 };
      if (userEmail) params.user_email = userEmail;
      const res = await api.get('/adani/recordings', { params });
      const body = res.data;
      console.log('[RecordingsPage] GET /adani/recordings response:', body);

      let list = [];
      if (Array.isArray(body)) list = body;
      else if (body && Array.isArray(body.data)) list = body.data;
      else if (body && Array.isArray(body.recordings)) list = body.recordings;
      else if (body && typeof body === 'object') {
        const arrField = Object.values(body).find(v => Array.isArray(v));
        if (arrField) list = arrField;
      }
      const mapped = list.map(r => {
        const rawId = (typeof r._id === 'object' && r._id?.$oid) ? r._id.$oid : (r._id || r.id || Math.random().toString(36).slice(2));
        const shortId = String(rawId).slice(-8);
        return {
        id: rawId,
        title: r.lead || r.title || `Recording #${shortId}`,
        status: capitalise(r.status || 'completed'),
        type: capitalise(r.type || 'audio'),
        duration: formatDuration(r.duration),
        durationRaw: r.duration,
        date: r.date || '',
        lead: r.lead || '',
        transcription: r.transcription || '',
        fileUrl: r.file_url || '',
        summary: r.transcription ? (r.transcription.length > 120 ? r.transcription.slice(0, 120) + '…' : r.transcription) : '',
        leadExtracted: !!r.lead,
      };
      });
      setRecordings(mapped);
    } catch (err) {
      console.error('Failed to fetch recordings:', err);
      setError('Could not load recordings');
      setRecordings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setRecordings([]);
    setExpandedId(null);
    fetchRecordings();
  }, [fetchRecordings]);

  const filtered = recordings.filter(r => {
    if (statusFilter !== 'All' && r.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!r.title.toLowerCase().includes(q) && !r.transcription.toLowerCase().includes(q) && !r.lead.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <div>
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="font-google text-[22px] font-semibold text-[var(--adani-text-primary)] leading-tight">
            Recordings
          </h1>
          <p className="text-[13px] text-[var(--adani-text-secondary)] mt-1">
            View and manage all your captured recordings
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={fetchRecordings} disabled={loading} className="btn-secondary text-[12px] px-3 py-2" title="Refresh">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={loading ? 'animate-spin' : ''}>
              <path d="M23 4v6h-6M1 20v-6h6" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div className="relative">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--adani-text-tertiary)]">
              <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
            </svg>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search recordings…"
              className="w-56 rounded-lg pl-9 pr-3 py-2 text-[13px] text-[var(--adani-text-primary)] placeholder-[var(--adani-text-tertiary)] glass-input"
            />
          </div>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="topbar-filter"
          >
            {['All', 'Completed', 'Processing', 'Failed'].map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {error && (
        <div className="glass-card-static rounded-xl p-4 mb-5 flex items-center gap-2">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--adani-danger)" strokeWidth="2"><path d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/></svg>
          <span className="text-[13px] text-[var(--adani-text-secondary)]">{error}</span>
          <button onClick={fetchRecordings} className="text-[12px] font-medium text-[var(--adani-primary)] ml-auto cursor-pointer">Retry</button>
        </div>
      )}

      <div className="glass-card-static rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr>
                <th className="table-header">Recording</th>
                <th className="table-header">Status</th>
                <th className="table-header">Type</th>
                <th className="table-header">Duration</th>
                <th className="table-header">Date</th>
                <th className="table-header">Lead</th>
              </tr>
            </thead>
            <tbody>
              {loading && recordings.length === 0 && (
                <tr>
                  <td colSpan={6}>
                    <div className="flex items-center justify-center gap-2 py-12">
                      <div className="h-5 w-5 rounded-full border-2 border-[var(--adani-primary)]/30 border-t-[var(--adani-primary)]" style={{ animation: 'pipSpin 0.8s linear infinite' }} />
                      <span className="text-[13px] text-[var(--adani-text-tertiary)]">Loading recordings…</span>
                    </div>
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6}>
                    <div className="empty-state">
                      <p className="empty-state-title">No recordings found</p>
                      <p className="empty-state-text">
                        {recordings.length === 0
                          ? 'Go to Capture to record or upload your first session'
                          : 'Try adjusting your search or filters'}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
              {filtered.map(r => (
                <React.Fragment key={r.id}>
                  <tr
                    className="table-row cursor-pointer"
                    onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                  >
                    <td className="table-cell">
                      <div className="flex items-center gap-3">
                        <div className={`status-dot ${
                          r.status === 'Completed' ? 'status-dot-success' :
                          r.status === 'Processing' ? 'status-dot-primary' :
                          'status-dot-danger'
                        }`} />
                        <div className="min-w-0">
                          <p className="text-[13px] font-medium text-[var(--adani-text-primary)] truncate max-w-[260px]">
                            {r.title}
                          </p>
                          {r.summary && (
                            <p className="text-[11px] text-[var(--adani-text-tertiary)] truncate max-w-[260px] mt-0.5">
                              {r.summary}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="table-cell"><StatusBadge status={r.status} /></td>
                    <td className="table-cell">
                      <span className="chip chip-primary text-[11px]">{r.type}</span>
                    </td>
                    <td className="table-cell text-[var(--adani-text-secondary)] tabular-nums text-[13px]">{r.duration}</td>
                    <td className="table-cell text-[var(--adani-text-secondary)] text-[13px]">{r.date}</td>
                    <td className="table-cell">
                      {r.leadExtracted ? (
                        <span className="badge badge-success text-[11px]">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>
                          {r.lead}
                        </span>
                      ) : (
                        <span className="text-[var(--adani-text-tertiary)] text-[13px]">—</span>
                      )}
                    </td>
                  </tr>
                  {expandedId === r.id && (r.transcription || r.fileUrl) && (
                    <tr>
                      <td colSpan={6} className="px-5 pb-4 pt-0">
                        <div className="glass-card-static rounded-xl p-4 animate-slide-up space-y-3">
                          {r.fileUrl && (
                            <div>
                              <p className="text-[12px] font-semibold text-[var(--adani-text-primary)] mb-2">File</p>
                              {r.type === 'Audio' ? (
                                <audio controls preload="metadata" className="w-full max-w-md" style={{ height: 36 }}>
                                  <source src={r.fileUrl} />
                                </audio>
                              ) : r.type === 'Image' ? (
                                <a href={r.fileUrl} target="_blank" rel="noopener noreferrer" className="inline-block">
                                  <img src={r.fileUrl} alt={r.title} className="max-h-40 rounded-lg border border-[var(--adani-border)]" />
                                </a>
                              ) : (
                                <a href={r.fileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--adani-primary)] hover:underline">
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                  Download file
                                </a>
                              )}
                            </div>
                          )}
                          {r.transcription && (
                            <div>
                              <p className="text-[12px] font-semibold text-[var(--adani-text-primary)] mb-2">Transcription</p>
                              <p className="text-[13px] leading-relaxed text-[var(--adani-text-secondary)] whitespace-pre-wrap">{r.transcription}</p>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
